// frontend/src/pages/ApiKeysPage.jsx
import React, { useState, useEffect } from 'react';
import api from '../services/api';
import {
  Key, Save, Eye, EyeOff, Lock, CheckCircle, XCircle,
  AlertTriangle, Loader, RefreshCw, RotateCcw, Server
} from 'lucide-react';

const ApiKeysPage = () => {
  const [apiKeys, setApiKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [restarting, setRestarting] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [restartResult, setRestartResult] = useState(null);

  const [editingKeys, setEditingKeys] = useState({});
  const [revealedKeys, setRevealedKeys] = useState({});
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  useEffect(() => { loadKeys(); }, []);

  const loadKeys = async () => {
    setLoading(true);
    try {
      const res = await api.get('/api/api-keys');
      setApiKeys(res.data);
    } catch (err) {
      setError('Failed to load API keys.');
    } finally {
      setLoading(false);
    }
  };

  const handleKeyEdit = (keyName, value) => {
    setEditingKeys(prev => ({ ...prev, [keyName]: value }));
  };

  const handleToggleReveal = (keyName) => {
    setRevealedKeys(prev => ({ ...prev, [keyName]: !prev[keyName] }));
  };

  const handleCancelEdit = (keyName) => {
    setEditingKeys(prev => { const n = { ...prev }; delete n[keyName]; return n; });
    setRevealedKeys(prev => { const n = { ...prev }; delete n[keyName]; return n; });
  };

  const handleSaveClick = () => {
    const hasChanges = Object.values(editingKeys).some(v => v && v.trim());
    if (!hasChanges) {
      setError('No changes to save — enter a new value for at least one key.');
      return;
    }
    setError('');
    setShowPasswordModal(true);
    setConfirmPassword('');
  };

  const handleConfirmSave = async () => {
    if (!confirmPassword) { setError('Please enter your password.'); return; }
    setSaving(true); setSuccess(''); setError(''); setRestartResult(null);
    try {
      const keysToUpdate = {};
      Object.entries(editingKeys).forEach(([k, v]) => { if (v && v.trim()) keysToUpdate[k] = v.trim(); });
      const res = await api.put('/api/api-keys', { password: confirmPassword, keys: keysToUpdate });
      setSuccess(res.data.message);
      setEditingKeys({});
      setRevealedKeys({});
      setShowPasswordModal(false);
      setConfirmPassword('');
      await loadKeys();
      setTimeout(() => setSuccess(''), 6000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to update API keys.');
    } finally {
      setSaving(false);
    }
  };

  const handleRestartServices = async () => {
    if (!window.confirm('Restart all server services now? The tool will be unavailable for a few seconds while services restart.')) return;
    setRestarting(true); setRestartResult(null); setError(''); setSuccess('');
    try {
      const res = await api.post('/api/api-keys/restart-services');
      setRestartResult(res.data);
    } catch (err) {
      // A restart will kill the connection mid-request — treat a network error as likely success
      if (err.code === 'ERR_NETWORK' || err.message?.includes('Network Error')) {
        setRestartResult({
          success: true,
          message: 'Services are restarting. The page will reload in 10 seconds.',
          services: {}
        });
        setTimeout(() => window.location.reload(), 10000);
      } else {
        setError(err.response?.data?.detail || 'Failed to restart services.');
      }
    } finally {
      setRestarting(false);
    }
  };

  const pendingCount = Object.values(editingKeys).filter(v => v && v.trim()).length;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader className="animate-spin text-brand-mid" size={32} />
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="mb-8 flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold text-brand-dark">API Keys</h1>
          <p className="text-gray-500 mt-1">Manage your API keys and credentials securely</p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={loadKeys}
            className="flex items-center gap-2 px-4 py-2 border border-gray-200 text-gray-600 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors text-sm"
          >
            <RefreshCw size={16} />Refresh
          </button>
          <button
            onClick={handleSaveClick}
            disabled={saving || pendingCount === 0}
            className="flex items-center gap-2 px-6 py-2 bg-brand-dark text-white rounded-lg cursor-pointer hover:bg-brand-mid disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <Save size={18} />
            {saving ? 'Saving...' : pendingCount > 0 ? `Save ${pendingCount} Change${pendingCount > 1 ? 's' : ''}` : 'Save Changes'}
          </button>
        </div>
      </div>

      {/* Messages */}
      {success && (
        <div className="bg-green-50 text-green-700 p-4 rounded-lg mb-6 flex items-center gap-2">
          <CheckCircle size={18} />{success}
        </div>
      )}
      {error && !showPasswordModal && (
        <div className="bg-red-50 text-red-600 p-4 rounded-lg mb-6 flex items-center gap-2">
          <XCircle size={18} />{error}
        </div>
      )}

      {/* Restart Result */}
      {restartResult && (
        <div className={`p-4 rounded-lg mb-6 flex items-start gap-3 ${restartResult.success ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-600'}`}>
          {restartResult.success ? <CheckCircle size={18} className="mt-0.5 shrink-0" /> : <XCircle size={18} className="mt-0.5 shrink-0" />}
          <div>
            <p className="font-medium">{restartResult.message}</p>
            {restartResult.services && Object.keys(restartResult.services).length > 0 && (
              <ul className="mt-2 space-y-1">
                {Object.entries(restartResult.services).map(([service, status]) => (
                  <li key={service} className="text-sm font-mono">
                    {status === 'restarted' ? '✓' : '✗'} {service}: {status}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}

      {/* Restart Services Panel */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5 mb-6">
        <div className="flex items-center justify-between">
          <div className="flex items-start gap-3">
            <div className="w-9 h-9 bg-brand-light rounded-lg flex items-center justify-center shrink-0">
              <Server size={16} className="text-brand-mid" />
            </div>
            <div>
              <p className="text-sm font-semibold text-gray-800">Restart Services</p>
              <p className="text-xs text-gray-500 mt-0.5">
                Required after saving new API keys. Restarts the backend, Celery worker, and Celery beat.
                The tool will be unavailable for a few seconds.
              </p>
            </div>
          </div>
          <button
            onClick={handleRestartServices}
            disabled={restarting}
            className="flex items-center gap-2 px-5 py-2 bg-amber-500 hover:bg-amber-600 text-white rounded-lg cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm font-medium whitespace-nowrap ml-4"
          >
            <RotateCcw size={16} className={restarting ? 'animate-spin' : ''} />
            {restarting ? 'Restarting...' : 'Restart Now'}
          </button>
        </div>
      </div>

      {/* Keys list */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 divide-y divide-gray-100">
        {apiKeys.map(item => (
          <div key={item.key} className="p-5">
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-start gap-3">
                <div className="w-9 h-9 bg-brand-light rounded-lg flex items-center justify-center shrink-0 mt-0.5">
                  <Key size={16} className="text-brand-mid" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-gray-800">{item.label}</p>
                  <p className="text-xs text-gray-500 mt-0.5">{item.description}</p>
                </div>
              </div>
              {item.is_configured ? (
                <span className="flex items-center gap-1 text-xs text-green-600 font-medium bg-green-50 border border-green-100 px-2.5 py-1 rounded-full shrink-0">
                  <CheckCircle size={11} />Configured
                </span>
              ) : (
                <span className="flex items-center gap-1 text-xs text-gray-400 font-medium bg-gray-50 border border-gray-200 px-2.5 py-1 rounded-full shrink-0">
                  <XCircle size={11} />Not set
                </span>
              )}
            </div>

            {/* Masked display + Update button */}
            {item.is_configured && editingKeys[item.key] === undefined && (
              <div className="flex items-center gap-2 ml-12">
                <code className="flex-1 px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm font-mono text-gray-500 tracking-wider">
                  {item.masked}
                </code>
                <button
                  onClick={() => setEditingKeys(prev => ({ ...prev, [item.key]: '' }))}
                  className="px-4 py-2 text-sm text-brand-mid hover:text-brand-dark border border-gray-200 rounded-lg hover:bg-brand-light transition-colors cursor-pointer whitespace-nowrap"
                >
                  Update
                </button>
              </div>
            )}

            {/* Edit input */}
            {(editingKeys[item.key] !== undefined || !item.is_configured) && (
              <div className="flex items-center gap-2 ml-12">
                <div className="relative flex-1">
                  <input
                    type={revealedKeys[item.key] ? 'text' : 'password'}
                    value={editingKeys[item.key] || ''}
                    onChange={e => handleKeyEdit(item.key, e.target.value)}
                    placeholder={item.is_configured ? 'Enter new value to replace existing key...' : 'Enter value...'}
                    className="w-full px-3 py-2 pr-10 border border-brand-mid rounded-lg focus:ring-2 focus:ring-brand-mid text-sm font-mono"
                    autoFocus={editingKeys[item.key] !== undefined}
                  />
                  <button
                    onClick={() => handleToggleReveal(item.key)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 cursor-pointer"
                    tabIndex={-1}
                  >
                    {revealedKeys[item.key] ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
                {item.is_configured && (
                  <button
                    onClick={() => handleCancelEdit(item.key)}
                    className="px-4 py-2 text-sm text-gray-500 hover:text-gray-700 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer"
                  >
                    Cancel
                  </button>
                )}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Security note */}
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mt-6">
        <p className="text-sm text-blue-700">
          <strong>Security:</strong> Keys are stored in the server environment file and never saved to the database.
          Only the first 8 characters are shown. Your admin password is required to make any changes.
        </p>
      </div>

      {/* Password Confirmation Modal */}
      {showPasswordModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl p-6 w-full max-w-md">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-brand-light rounded-full flex items-center justify-center">
                <Lock size={20} className="text-brand-dark" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-brand-dark">Confirm Your Password</h3>
                <p className="text-sm text-gray-500">Required to update API keys</p>
              </div>
            </div>
            <p className="text-sm text-gray-600 mb-4">
              You are about to update <strong>{pendingCount} API key{pendingCount > 1 ? 's' : ''}</strong>. Please enter your admin password to confirm.
            </p>
            <div className="relative mb-4">
              <input
                type={showConfirmPassword ? 'text' : 'password'}
                value={confirmPassword}
                onChange={e => setConfirmPassword(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleConfirmSave()}
                placeholder="Enter your password"
                className="w-full px-4 py-3 pr-12 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-mid"
                autoFocus
              />
              <button
                onClick={() => setShowConfirmPassword(p => !p)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 cursor-pointer"
              >
                {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
            {error && (
              <div className="bg-red-50 text-red-600 p-3 rounded-lg mb-4 text-sm flex items-center gap-2">
                <XCircle size={16} />{error}
              </div>
            )}
            <div className="flex gap-3">
              <button
                onClick={handleConfirmSave}
                disabled={saving || !confirmPassword}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-brand-dark text-white rounded-lg cursor-pointer hover:bg-brand-mid disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium"
              >
                <Save size={18} />{saving ? 'Saving...' : 'Confirm & Save'}
              </button>
              <button
                onClick={() => { setShowPasswordModal(false); setConfirmPassword(''); setError(''); }}
                className="px-4 py-3 border border-gray-300 text-gray-600 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ApiKeysPage;